"use client";

import { useAccount, useChainId, useConnect, useDisconnect, useWalletClient } from "wagmi";
import { useAuthenticate } from "@account-kit/react";
import { useCallback, useRef } from "react";
import { chains } from "@shared/src/config/chains";
import { AccountType } from "@shared/src/lib/types";
import { getErrorMessage, getWalletTypefromParams } from "@shared/src/utils";
import { ixToast } from "@shared/src/utils/ixToast";
import {
  AuthParams as AuthParams,
  PasskeyAuthParams,
  EmailAuthParams,
  WalletAuthParams,
} from "@shared/src/hooks/auth/types";
import { useAccountManagement } from "@shared/src/hooks/auth/useAccountManagement";
import { getJwtWithSiwe as getJwtWithSiwe, extractAuthParams } from "@shared/src/hooks/auth/utils";
import { getEoaOrScaSigner, poll, pollForAuthDetails } from "@shared/src/lib/auth";
import { IxSigner, useAuthStore } from "@shared/src/stores/useAuthStore";
import { handleWalletConnection, retryWalletConnection } from "@shared/src/hooks/auth/walletManager";
import { settingsService } from "@shared/src/services/settings.service";
import { AlchemySigner } from "@account-kit/core";
import { alchemy } from "@account-kit/infra";
import { useHostStore } from "@shared/src/stores/useHostStore";
import useUserAgent from "../useUserAgent";
import { alchemyChains } from "@shared/src/config/alchemy";
import { create } from "domain";
import { createWalletClient } from "viem";

export default function useAuth() {
  const sdk = useHostStore(state => state.sdk);
  const { authenticateAsync } = useAuthenticate();
  const { handleAccountSetup } = useAccountManagement();
  const { setWalletStatus, setSiweActions, closeAndResetAuthModal } = useAuthStore();
  const { connector } = useAccount();
  const { connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { isDesktop, userAgent, isMobile } = useUserAgent();
  const { data: walletClient } = useWalletClient();

  const handleIxAuth = useCallback(
    async (signer: IxSigner, params: AuthParams, connectorIcon?: string) => {
      let email: string | undefined;
      if (params.type === "oauth" || params.type === "otp" || params.type === "passkey") {
        const authDetails = await pollForAuthDetails();
        // Extract email from authDetails for OAuth
        if (authDetails?.email) {
          email = authDetails.email;
          useAuthStore.getState().setEmail(email);
        }
      }

      console.info(params.type, signer);
      const address = await signer.getAddress();
      useAuthStore.setState({
        walletStatus: "siwe_request",
        walletName: params.type === "oauth" ? params.authProviderId : "Wallet",
        walletIcon: connectorIcon,
      });

      const resolve = async () => {
        try {
          setWalletStatus("siwe_confirmation", "Wallet");
          const sdk = useHostStore.getState().sdk;
          const chainId =
            connector?.id === "baseAccount"
              ? sdk?.config.chains.find(c => c.name.toLowerCase().includes("base"))?.id
              : useHostStore.getState().activeChain?.id || chains[0].id;
          if (!chainId) throw new Error("unable to find chainId when signing in");
          console.log({ walletClient, signer });

          const jwt = await getJwtWithSiwe(signer, address, chainId);
          const accountResponse = await settingsService.getAccount(jwt);

          await handleAccountSetup({
            jwt,
            address,
            signer: signer,
            accountType: getWalletTypefromParams(params),
            existingAccount: accountResponse.data,
            params: { ...params, connectorIcon: connectorIcon || "" },
          });
        } catch (e: any) {
          ixToast.error(getErrorMessage(e));
          setWalletStatus("error", connector?.name || "Wallet");
          throw e;
        }
      };
      const reject = () => {
        if (getWalletTypefromParams(params) === "eoa") {
          connector?.disconnect();
          closeAndResetAuthModal();
        } else {
          (signer as AlchemySigner)?.disconnect();
        }
      };
      if (getWalletTypefromParams(params) === "eoa") {
        setSiweActions({
          resolve,
          reject,
        });
      } else {
        const t = ixToast.loading("Signing In");
        await resolve().finally(() => {
          t.dismiss();
        });
      }
    },
    [handleAccountSetup, setWalletStatus, setSiweActions, closeAndResetAuthModal, connector, walletClient],
  );
  //@dev:do not perform any async operations before this call else popup will be blocked
  const authenticate = useCallback(
    async (params: AuthParams) => {
      const { type, emailMode, authProviderId, mode, connectorIcon: _connectorIcon } = extractAuthParams(params);
      async function requestOTPCode(email: string) {
        authenticateAsync({
          type: "email",
          email: email,
          emailMode,
        }).catch(error => {
          ixToast.error(error);
        });
        await new Promise(resolve => {
          setTimeout(() => {
            resolve(null);
          }, 1000);
        });
        return;
      }

      function constructRedirectUrl(connectorIcon: string, authProviderId, email?: string) {
        const url = new URL(window.location.href);
        url.searchParams.set("connectorIcon", connectorIcon);
        url.searchParams.set("authProviderId", authProviderId || "");
        url.searchParams.set("oauth", "true");
        if (email) {
          url.searchParams.set("email", email);
        }
        return url.toString();
        ``;
      }

      const authenticateAlchemy = async () => {
        switch (params.type) {
          case "oauth":
            console.log("initialSignInToGetSigner, oauth", params);
            // do not perform any async operations before this call else popus will be blocked
            isDesktop
              ? await authenticateAsync({
                  type: params.type,
                  authProviderId: authProviderId!,
                  mode: "popup",
                })
              : authenticateAsync({
                  type: params.type,
                  authProviderId: authProviderId!,
                  mode: "redirect",
                  redirectUrl: constructRedirectUrl(params.connectorIcon, authProviderId),
                });

            return;
          case "otp":
            console.trace("initialSignInToGetSigner, otp", params);
            const user = await authenticateAsync({
              type: params.type,
              otpCode: params.otpCode,
            });
            return;
          case "passkey":
            console.log("initialSignInToGetSigner, passkey", params);
            return await authenticateAsync({
              type: params.type,
              email: params.email,
            });
          default:
            console.log("initialSignInToGetSigner, default", params);
            throw new Error("Invalid authentication type");
        }
      };
      async function initialSignInToGetSigner() {
        switch (params.type) {
          case "wallet":
            return await handleWalletConnection(params.connector, connectAsync, disconnectAsync);
          case "walletConnect":
            return getEoaOrScaSigner("eoa", params.provider);
          default:
            // do not perform any async operations before this call else popus will be blocked
            const user = await authenticateAlchemy();
            console.log("signer user inauth", user);
            if (params.type === "email") {
              return;
            }
            const signer = await poll(
              async () => {
                const signer = getEoaOrScaSigner("sca");
                const det = await (signer as AlchemySigner)?.getAuthDetails();
                if (!signer || !det.userId) throw new Error("Failed to get Alchemy signer");
                return signer;
              },
              1000,
              10,
            );
            return signer;
        }
      }

      try {
        console.log("authenticate initialSignInToGetSigner requestOTPCode", params);
        if (params.type === "email") {
          await requestOTPCode(params.email);
          console.log("requestOTPCode", params);
        } else {
          const signer = await initialSignInToGetSigner();
          console.log("signer inauth", signer);
          if (!signer) throw new Error("Error Signing In. Please try again");
          await handleIxAuth(signer as IxSigner, params, _connectorIcon);
        }
      } catch (error: any) {
        ixToast.error(getErrorMessage(error));
        console.error(error);
        // Set error status before throwing
        const walletName = params.type === "wallet" ? params.connector.name : params.type;
        setWalletStatus("error", walletName);
      }
    },
    [authenticateAsync, setWalletStatus, connectAsync, handleIxAuth, disconnectAsync],
  );

  return {
    authenticate,
    retryWalletConnection: (walletName: string) => retryWalletConnection(walletName, authenticate),
    handleIxAuth,
  };
}
